import { useEffect, useRef, useState } from 'react';

import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { getTimeDifferenceString } from '@/helpers';
import { RefreshLottieIcon } from '@/modules/common/components/atoms';

enum RefreshPhase {
  Idle = 'idle',
  Loading = 'loading',
  Success = 'success',
}

const MIN_LOADING_MS = 400;
const SUCCESS_DISPLAY_MS = 1800;

export interface GenerateEntityRefreshButtonProps {
  /** True when a divergence between loaded data and server data has been detected */
  hasStaleData: boolean;
  /** True while a refresh is in-flight */
  isRefreshing: boolean;
  /** Timestamp of the last successful data fetch */
  dataUpdatedAt: Date | number | null | undefined;
  /** Triggers the smart-merge refresh — may be sync or async */
  onRefresh: () => void | Promise<void>;
  /** Tooltip explaining why a refresh is recommended */
  tooltipDescription: string;
  /** Tooltip body shown while the refresh is in progress */
  refreshingLabel: string;
  /** Button label (idle state) — e.g. "Refresh" */
  label: string;
  /** Button label shown after a successful refresh */
  successLabel?: string;
}

/**
 * Compact icon-only refresh affordance shown next to the filters row.
 *
 * Hidden when there's nothing to refresh: only renders when `hasStaleData` is
 * true (or a refresh is currently in-flight). This avoids consuming vertical
 * space with a permanent alert banner on small laptops.
 */
export const GenerateEntityRefreshButton = ({
  hasStaleData,
  isRefreshing,
  dataUpdatedAt,
  onRefresh,
  tooltipDescription,
  refreshingLabel,
  label,
  successLabel,
}: GenerateEntityRefreshButtonProps) => {
  const { t } = useTranslation('common');
  const [timeDiff, setTimeDiff] = useState<string | null>(getTimeDifferenceString(dataUpdatedAt));

  // Phase is driven entirely by handleClick — never synced from isRefreshing.
  // React batches setIsRefreshing(true/false) in the same tick for sync work,
  // so the prop never actually flips and can't be used to detect completion.
  const [phase, setPhase] = useState<RefreshPhase>(RefreshPhase.Idle);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isRefreshing) {
      setTimeDiff(null);
      return;
    }

    setTimeDiff(getTimeDifferenceString(dataUpdatedAt));
    const interval = setInterval(() => {
      setTimeDiff(getTimeDifferenceString(dataUpdatedAt));
    }, 1000);

    return () => clearInterval(interval);
  }, [dataUpdatedAt, isRefreshing]);

  const handleClick = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const start = Date.now();
    setPhase(RefreshPhase.Loading);

    await onRefresh();

    const elapsed = Date.now() - start;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);

    timerRef.current = setTimeout(() => {
      setPhase(RefreshPhase.Success);
      timerRef.current = setTimeout(() => {
        setPhase(RefreshPhase.Idle);
      }, SUCCESS_DISPLAY_MS);
    }, remaining);
  };

  const isLoading = phase === RefreshPhase.Loading;
  const isSuccess = phase === RefreshPhase.Success;
  // Stay visible while loading/success even if hasStaleData flipped to false
  const isVisible = hasStaleData || isLoading || isSuccess;

  if (!isVisible) return null;

  const currentLabel = isLoading ? refreshingLabel : isSuccess ? (successLabel ?? label) : label;

  const tooltipContent = (
    <div className="flex max-w-[280px] flex-col gap-1 text-xs">
      <p className="font-medium">
        {isLoading
          ? refreshingLabel
          : isSuccess
            ? (successLabel ?? tooltipDescription)
            : tooltipDescription}
      </p>
      {timeDiff && !isLoading && !isSuccess && (
        <p className="text-muted-foreground">
          {t('refreshData.refreshStatus', { time: timeDiff })}
        </p>
      )}
    </div>
  );

  return (
    <SimpleTooltip content={tooltipContent}>
      <span className="relative inline-flex">
        <Button
          variant="outline"
          size="sm"
          onClick={handleClick}
          disabled={isLoading || isSuccess}
          className="h-9 flex-shrink-0 gap-2 px-3"
          aria-label={currentLabel}
        >
          {isLoading || isSuccess ? (
            <RefreshLottieIcon phase={isLoading ? 'loading' : 'success'} size={16} />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span>{currentLabel}</span>
        </Button>
        {hasStaleData && !isLoading && !isSuccess && (
          <span
            aria-hidden
            className="absolute -right-1 -top-1 h-2.5 w-2.5 animate-pulse rounded-full bg-primary ring-2 ring-surface"
          />
        )}
      </span>
    </SimpleTooltip>
  );
};
